
import { useCallback } from "react";
import { ExperienceItem } from "@/hooks/useProfile";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { usePersistentFormState } from "@/hooks/dashboard/usePersistentFormState";
import { Json } from "@/services/profiles/types";

interface ExperienceState {
  isAdding: boolean;
  isImporting: boolean;
  isSaving: boolean;
  newlyAddedIds: string[];
}

// Update this interface to match Json signature
interface ExperienceItemForDb {
  id: string;
  position: string;
  company: string;
  start_date: string;
  end_date: string | null;
  current: boolean;
  description: string;
  location: string;
  [key: string]: Json; // Add index signature to satisfy Json type
}

export function useExperienceSection(
  initialItems: ExperienceItem[] | null = [],
  onExperienceChange: (items: ExperienceItem[]) => void,
  userId?: string,
  sectionId: string = "experience"
) {
  const initialState: ExperienceState = {
    isAdding: false,
    isImporting: false,
    isSaving: false,
    newlyAddedIds: [],
  };

  const [state, setState, resetState] = usePersistentFormState<ExperienceState>(
    initialState,
    "experience_state",
    sectionId
  );

  const { isAdding, isImporting, isSaving, newlyAddedIds } = state;

  const validateDateRange = useCallback(
    (
      startDate: string,
      endDate: string | null,
      current: boolean
    ): string | null => {
      if (startDate && endDate && !current) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (end < start) {
          return "End date cannot be before start date";
        }
      }
      return null;
    },
    []
  );

  const saveExperienceToDatabase = useCallback(
    async (items: ExperienceItem[], id: string) => {
      try {
        setState((prev) => ({ ...prev, isSaving: true }));

        const experienceItemsForDb = items.map(
          (item) => ({
            id: item.id,
            position: item.position,
            company: item.company,
            start_date: item.start_date,
            end_date: item.current ? null : item.end_date,
            current: item.current || false,
            description: item.description || "",
            location: item.location || "",
          }) as ExperienceItemForDb
        );

        const { error } = await supabase
          .from("profiles")
          .update({
            experience_items: experienceItemsForDb as unknown as Json[],
            updated_at: new Date().toISOString(),
          })
          .eq("id", id);

        if (error) throw error;

        toast.success("Your experience has been updated successfully.");

        resetState();
      } catch (error) {
        toast.error(error.message || "An error occurred.");
      } finally {
        setState((prev) => ({ ...prev, isSaving: false }));
      }
    },
    [setState, toast, resetState]
  );

  const handleAddItem = useCallback(
    async (newItem: ExperienceItem) => {
      if (
        !newItem.position.trim() ||
        !newItem.company.trim() ||
        !newItem.start_date
      )
        return;

      const dateError = validateDateRange(
        newItem.start_date,
        newItem.end_date,
        newItem.current
      );
      if (dateError) {
        toast.error(dateError);
        return;
      }

      const itemId = newItem.id || uuidv4();
      const updatedItem = { ...newItem, id: itemId };

      setState((prev) => ({
        ...prev,
        isAdding: false,
        newlyAddedIds: [...prev.newlyAddedIds, itemId],
      }));

      const updatedItems = [...(initialItems || []), updatedItem];
      onExperienceChange(updatedItems);

      if (userId) {
        await saveExperienceToDatabase(updatedItems, userId);
      }
    },
    [
      initialItems,
      onExperienceChange,
      userId,
      toast,
      validateDateRange,
      setState,
    ]
  );

  const handleRemoveItem = useCallback(
    async (index: number) => {
      const updatedItems = [...(initialItems || [])];
      updatedItems.splice(index, 1);
      onExperienceChange(updatedItems);

      if (userId) {
        await saveExperienceToDatabase(updatedItems, userId);
      }
    },
    [initialItems, onExperienceChange, userId, saveExperienceToDatabase]
  );

  const setIsAdding = useCallback(
    (value: boolean) => {
      setState((prev) => ({ ...prev, isAdding: value }));
    },
    [setState]
  );

  const handleAddFirstExperience = useCallback(() => {
    setState((prev) => ({ ...prev, isAdding: true }));
  }, [setState]);

  return {
    isAdding,
    isImporting,
    isSaving,
    newlyAddedIds,
    setIsAdding,
    handleAddItem,
    handleRemoveItem,
    handleAddFirstExperience,
    resetState,
  };
}
